import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Button3D — herbruikbare 3D-knop met dezelfde recipe als BadgeStatus3D:
 * inset highlight + drop-shadow + top-highlight overlay.
 * Tone-kleuren consistent met de status-badges.
 */
export type Button3DTone = "primary" | "violet" | "neutral" | "danger";

const TONE: Record<Button3DTone, { bg: string; ring: string; text: string }> = {
  primary: { bg: "bg-slate-700 hover:bg-slate-800", ring: "ring-slate-400/50", text: "text-white" },
  violet:  { bg: "bg-violet-500 hover:bg-violet-600", ring: "ring-violet-300/50", text: "text-white" },
  neutral: { bg: "bg-slate-200 hover:bg-slate-300", ring: "ring-slate-300/60", text: "text-slate-800" },
  danger:  { bg: "bg-red-500 hover:bg-red-600", ring: "ring-red-300/50", text: "text-white" },
};

export interface Button3DProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Button3DTone;
  iconOnly?: boolean;
}

export const Button3D = React.forwardRef<HTMLButtonElement, Button3DProps>(
  ({ tone = "primary", className, children, iconOnly, disabled, ...rest }, ref) => {
    const t = TONE[tone];
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          "relative inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm",
          iconOnly ? "h-10 w-10" : "h-10 px-4",
          "ring-1 transition-all duration-150",
          "shadow-[0_3px_6px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.4),inset_0_-1px_0_rgba(0,0,0,0.18)]",
          "hover:-translate-y-0.5 hover:shadow-[0_5px_10px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(255,255,255,0.45),inset_0_-1px_0_rgba(0,0,0,0.18)]",
          "active:translate-y-0 active:shadow-[0_2px_4px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.3)]",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0",
          t.bg, t.ring, t.text,
          className,
        )}
        {...rest}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-full bg-white/25 blur-[1px]"
        />
        <span className="relative flex items-center gap-2">{children}</span>
      </button>
    );
  },
);
Button3D.displayName = "Button3D";

export default Button3D;
